import { useNotification } from "@context/NotificationContext";
import {
  invalidateLicenseListQueries,
  notifyVendorLicenseRequestError,
} from "@pages/vendor-license-request/hooks/useCreateVendorLicenseRequest";
import type { VendorLicenseRequestPayload } from "@services/licensing/types";
import { createVendorLicenseRenewalRequest } from "@services/licensingApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

export const useCreateVendorLicenseRenewalRequest = (licenseId: string) => {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VendorLicenseRequestPayload) =>
      runTransaction(
        {
          module: "licensing",
          screen: "VendorLicenseRenewal",
          action: "submit_vendor_license_renewal_request",
          entityType: "license_request",
          entityId: licenseId,
        },
        (transactionId) =>
          createVendorLicenseRenewalRequest({
            licenseId,
            payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      await invalidateLicenseListQueries(queryClient);
      notify({
        title: t("licensing.notifications.vendor-license-renewal-submit-success-title"),
        description: t("licensing.notifications.vendor-license-renewal-submit-success-description"),
        type: "success",
      });
      navigate(`/${paths.licenses.index}`);
    },
    onError: (err) => {
      notifyVendorLicenseRequestError(notify, err);
    },
  });
};
